import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

const confirmationMessage = "Changes you made may not be saved.";

/**
 * Hook that provides unsaved changes warning functionality for both navigation and browser close events
 * Uses window.confirm for a unified experience across all scenarios
 */
export const useUnsavedChangesWarning = (hasUnsavedChanges: boolean, onProceed: () => void): void => {
  // Handle browser close/refresh events
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Standard beforeunload behavior - browser shows its own dialog
        event.preventDefault();
        return confirmationMessage;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Handle navigation blocking with window.confirm
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.state?.date?.getTime() !== nextLocation.state?.date?.getTime())
  );

  // Handle the blocked navigation with window.confirm
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const shouldLeave = window.confirm(confirmationMessage);
      if (shouldLeave) {
        onProceed();
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);
};
